/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { fetchApiData } from "constants/api/ApiHelper";
import { post } from "constants/api/ApiClient";
import EndpointProps from "interface/EndpointProps";
import SignUpRequest from "constants/api/models/SignUpRequest";
import { LegacySignUpResponse } from "constants/api/models/SignUpResponse";

/**
 * Simplified API handler for user signup.
 * 
 * This function performs the API call to submit the signup details
 * and processes the response minimally.
 * 
 * @async
 * @function signUpUser
 * @param {EndpointProps & { data: SignUpRequest }} params - The request parameters containing a unique tag and signup payload.
 * @returns {Promise<{ data: LegacySignUpResponse | null; error: string | null }} - A promise that resolves to an object containing the signup response data or an error message.
 */
export async function signUpUser({
    tag,
    data,
}: EndpointProps & { data: SignUpRequest }): Promise<{ data: LegacySignUpResponse | null; error: string | null; detail?: string | null }> {
    if (!data) {
        return {
            data: null,
            error: "Invalid signup request: Missing data payload.",
        };
    }

    const result = await fetchApiData<LegacySignUpResponse>(
        tag,
        () =>
            post<LegacySignUpResponse>({
                tag,
                url: "/auth/signup",
                body: data,
            }),
        (responseData) => responseData
    );

    if (result.response.status === "error") {
        return {
            data: null,
            error: result.response.message || "An error occurred during signup.",
            // The server's specific complaint (for example which field was rejected); shown to the person in plain words.
            detail: result.response.error ?? null,
        };
    }

    return {
        ...result.response.data || null,
        error: null,
    };
}

/**
 * Interface representing the response from a login API endpoint.
 */
export interface LoginResponse {
    /**
     * The status of the login attempt.
     */
    status: string;

    /**
     * A message providing additional information about the login attempt.
     */
    message: string;

    /**
     * The timestamp when the response was generated.
     */
    timestamp: string;

    /**
     * True if this account was created with a system-generated password
     * (e.g. a Pilot admin-created account) that must be replaced before the
     * user can proceed - never their own choice.
     */
    data?: {
        mustChangePassword?: boolean;
    };
}

/**
 * Logs in a user with the provided email and password.
 *
 * @param {EndpointProps & { data: { email: string, password: string }}} params - The parameters for the login request.
 * @param {string} params.tag - The tag for the endpoint.
 * @param {Object} params.data - The data payload for the login request.
 * @param {string} params.data.email - The email of the user.
 * @param {string} params.data.password - The password of the user.
 * @returns {Promise<{ success: boolean, error: string | null, mustChangePassword?: boolean }>} A promise that resolves to an object indicating the success or failure of the login attempt.
 * @throws Will throw an error if the fetchApiData or post function fails.
 */
export async function logInUser({ tag, data }: EndpointProps & { data: { email: string, password: string }}): Promise<{ success: boolean, error: string | null, detail?: string | null, mustChangePassword?: boolean }> {
    if (!data) {
        return {
            success: false,
            error: "Invalid login request: Missing data payload.",
        };
    }

    const result = await fetchApiData<LoginResponse>(
        tag,
        () =>
            post<LoginResponse>({
                tag,
                url: '/auth/login',
                body: data
            }),
        (responseData) => responseData.data
    )

    if (result.response.status === 'error') {
        return {
            success: false,
            error: result.response.message || 'An error occrred during login.',
            detail: result.response.error ?? null
        }
    }

    return {
        success: true,
        error: null,
        mustChangePassword: result.response.data?.mustChangePassword ?? false
    }
}

/**
 * Sets a new password for the currently authenticated user (the stored
 * session JWT is attached automatically by getDefaultHeaders). Used by the
 * forced first-login password-change flow for Pilot admin-created accounts.
 *
 * @param {EndpointProps & { data: { newPassword: string; confirmPassword: string } }} params
 * @returns {Promise<{ success: boolean; error: string | null }>}
 */
export async function changePassword({ tag, data }: EndpointProps & { data: { newPassword: string; confirmPassword: string } }): Promise<{ success: boolean; error: string | null }> {
    const result = await fetchApiData(
        tag,
        () => post({ tag, url: '/auth/change-password', body: data }),
        (d) => d
    )
    if (result.response.status === 'error') return { success: false, error: result.response.message || 'Failed to update password.' }
    return { success: true, error: null }
}

// ─── OTP ─────────────────────────────────────────────────────────────────────

export async function sendEmailOtp({ tag }: EndpointProps): Promise<{ success: boolean; alreadyVerified?: boolean; error: string | null }> {
    const result = await fetchApiData<{ alreadyVerified?: boolean }>(
        tag,
        () => post({ tag, url: '/otp/send', body: {} }),
        (d) => d
    )
    if (result.response.status === 'error') return { success: false, error: result.response.message || 'Failed to send code.' }
    return { success: true, alreadyVerified: result.response.data?.alreadyVerified, error: null }
}

export async function verifyEmailOtp({ tag, data }: EndpointProps & { data: { code: string } }): Promise<{ success: boolean; error: string | null }> {
    const result = await fetchApiData(
        tag,
        () => post({ tag, url: '/otp/verify', body: data }),
        (d) => d
    )
    if (result.response.status === 'error') return { success: false, error: result.response.message || 'Verification failed.' }
    return { success: true, error: null }
}

export async function resendEmailOtp({ tag }: EndpointProps): Promise<{ success: boolean; error: string | null }> {
    const result = await fetchApiData(
        tag,
        () => post({ tag, url: '/otp/resend', body: {} }),
        (d) => d
    )
    if (result.response.status === 'error') return { success: false, error: result.response.message || 'Failed to resend code.' }
    return { success: true, error: null }
}

// ─── Forgot Password ──────────────────────────────────────────────────────────

export async function forgotPasswordSend({ tag, data }: EndpointProps & { data: { email: string } }): Promise<{ success: boolean; error: string | null }> {
    const result = await fetchApiData(
        tag,
        () => post({ tag, url: '/auth/forgot-password/send', body: data }),
        (d) => d
    )
    if (result.response.status === 'error') return { success: false, error: result.response.message || 'Failed to send code.' }
    return { success: true, error: null }
}

export async function forgotPasswordVerify({ tag, data }: EndpointProps & { data: { email: string; code: string } }): Promise<{ resetToken: string | null; error: string | null }> {
    const result = await fetchApiData<{ resetToken: string }>(
        tag,
        () => post({ tag, url: '/auth/forgot-password/verify', body: data }),
        (d) => d
    )
    if (result.response.status === 'error') return { resetToken: null, error: result.response.message || 'Verification failed.' }
    const envelope = result.response.data
    return { resetToken: envelope?.data?.resetToken ?? null, error: null }
}

export async function forgotPasswordReset({ tag, data }: EndpointProps & { data: { resetToken: string; newPassword: string; confirmPassword: string } }): Promise<{ success: boolean; error: string | null }> {
    const result = await fetchApiData(
        tag,
        () => post({ tag, url: '/auth/forgot-password/reset', body: data }),
        (d) => d
    )
    if (result.response.status === 'error') return { success: false, error: result.response.message || 'Failed to reset password.' }
    return { success: true, error: null }
}

/* eslint-enable @typescript-eslint/no-redundant-type-constituents */
/* eslint-enable @typescript-eslint/no-unsafe-return */
/* eslint-enable @typescript-eslint/no-unsafe-argument */
